import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Container,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authenticatedFetch } from '../../utils/api';
import { getAvailableRoles, getRoleDisplayName, Role } from '../../utils/permissions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Company {
  id: number;
  name: string;
}

interface User {
  id: number;
  email: string;
  roles: string[];
  company_id?: number | null;
  company?: {
    id: number;
    name: string;
  };
  created_at: string;
  updated_at: string;
}

const AVAILABLE_ROLES = getAvailableRoles();

const userSchema = z.object({
  email: z.string().email('Must be a valid email address'),
  password: z.string().optional().or(z.literal('')),
  password_confirmation: z.string().optional().or(z.literal('')),
  roles: z.array(z.string()).min(1, 'User must have at least one role'),
  company_id: z.number().nullable().optional()
}).refine((data) => {
  if (data.password && data.password_confirmation) {
    return data.password === data.password_confirmation;
  }
  return true;
}, {
  message: "Passwords don't match",
  path: ["password_confirmation"],
}).refine((data) => {
  // If company_rep is selected (and no admin/employee), company_id is required
  const isCompanyRepOnly = data.roles.includes('company_rep') && 
    !data.roles.includes('admin') && 
    !data.roles.includes('employee');
  if (isCompanyRepOnly && !data.company_id) {
    return false;
  }
  return true;
}, {
  message: "Company is required for Company Representative role",
  path: ["company_id"],
});

type UserFormData = z.infer<typeof userSchema>;

const UserDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const { control, handleSubmit, reset, watch } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
  });

  const selectedRoles = watch('roles');
  const isCompanyRepSelected = selectedRoles?.includes('company_rep') && 
    !selectedRoles?.includes('admin') && 
    !selectedRoles?.includes('employee');

  useEffect(() => {
    fetchUser();
  }, [id]);

  useEffect(() => {
    if (isEditing && isCompanyRepSelected) {
      fetchCompanies();
    }
  }, [isEditing, isCompanyRepSelected]);

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await authenticatedFetch(`${API_URL}/companies?per_page=1000&minimal=true`);
      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }
      const data = await response.json();
      setCompanies(data);
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/users/${id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }

      const data = await response.json();
      setUser(data);
      reset({
        email: data.email,
        password: '',
        password_confirmation: '',
        roles: data.roles || ['admin'],
        company_id: data.company_id || null
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (data: UserFormData) => {
    try {
      const response = await authenticatedFetch(`${API_URL}/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ user: data }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
      setIsEditing(false);
      setError('');
      // Reset password fields after successful update
      reset({
        email: updatedUser.email,
        password: '',
        password_confirmation: '',
        roles: updatedUser.roles || ['admin'],
        company_id: updatedUser.company_id || null
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await authenticatedFetch(`${API_URL}/users/${id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error('Failed to delete user');
        }

        navigate('/users');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {isEditing ? 'Edit User' : 'User Details'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isEditing ? (
            <Box component="form" onSubmit={handleSubmit(handleUpdate)} sx={{ mt: 2 }}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Password (leave blank to keep current)"
                    type="password"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="password_confirmation"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Password Confirmation"
                    type="password"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    margin="normal"
                  />
                )}
              />

              <Controller
                name="roles"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl 
                    component="fieldset" 
                    sx={{ mt: 2, mb: 2 }}
                    error={!!fieldState.error}
                  >
                    <FormLabel component="legend">Roles</FormLabel>
                    <FormGroup>
                      {AVAILABLE_ROLES.map((role) => (
                        <FormControlLabel
                          key={role}
                          control={
                            <Checkbox
                              checked={field.value?.includes(role) || false}
                              onChange={(e) => {
                                const newRoles = e.target.checked
                                  ? [...(field.value || []), role]
                                  : (field.value || []).filter((r: string) => r !== role);
                                field.onChange(newRoles);
                              }}
                            />
                          }
                          label={getRoleDisplayName(role as Role)}
                        />
                      ))}
                    </FormGroup>
                    {fieldState.error && (
                      <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              {isCompanyRepSelected && (
                <Controller
                  name="company_id"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormControl 
                      fullWidth 
                      margin="normal"
                      error={!!fieldState.error}
                    >
                      <InputLabel id="company-select-label">Company *</InputLabel>
                      <Select
                        {...field}
                        labelId="company-select-label"
                        label="Company *"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                        disabled={loadingCompanies}
                      >
                        {loadingCompanies ? (
                          <MenuItem value="" disabled>Loading companies...</MenuItem>
                        ) : companies.length === 0 ? (
                          <MenuItem value="" disabled>No companies available</MenuItem>
                        ) : (
                          companies.map((company) => (
                            <MenuItem key={company.id} value={company.id}>
                              {company.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      <FormHelperText>
                        {fieldState.error?.message || 'Select the company this representative belongs to'}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setIsEditing(false);
                    setError('');
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {user?.email}
                </Typography>

                <Typography variant="subtitle1" color="text.secondary">
                  Roles
                </Typography>
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {user?.roles && user.roles.length > 0 ? (
                    user.roles.map((role) => (
                      <Chip 
                        key={role} 
                        label={getRoleDisplayName(role as Role)}
                        color={role === 'admin' ? 'primary' : role === 'company_rep' ? 'secondary' : 'default'}
                        size="small"
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No roles assigned
                    </Typography>
                  )}
                </Box>

                {user?.company && (
                  <>
                    <Typography variant="subtitle1" color="text.secondary">
                      Company
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {user.company.name}
                    </Typography>
                  </>
                )}

                <Typography variant="subtitle1" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(user?.created_at || '').toLocaleDateString()}
                </Typography>

                <Typography variant="subtitle1" color="text.secondary">
                  Updated At
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(user?.updated_at || '').toLocaleDateString()}
                </Typography>
              </Box>

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={handleDelete}
                >
                  Delete
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/users')}
                >
                  Back to List
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default UserDetails;

